<template>
  <tr
    :aria-label="
      isClickable ? i18n(translations.goToConversationAriaLabel) : undefined
    "
    class="border-static-default-low text-static-default-mid focus-visible-outline-default-hi body-1 border-b hover:bg-static-default-low-hover"
    :class="{ 'hover:cursor-pointer': isClickable }"
    data-test="open-tasks-row"
    :role="isClickable ? 'button' : undefined"
    :tabindex="isClickable ? 0 : undefined"
    @click="handleRowClick"
    @keydown.enter.prevent="handleRowClick"
    @keydown.space.prevent="handleRowClick"
  >
    <td :class="tableCellClassNames">
      <TaskStatus
        v-if="!isUploadPendingOrSuccess"
        :deadline="request.deadline"
        :status="request.status"
      />

      <UploadStatus
        v-if="isUploadPendingOrSuccess"
        :is-upload-pending
        :is-upload-success
      />
    </td>
    <td class="body-1-bold" :class="tableCellClassNames">
      <div v-if="!isUploadPendingOrSuccess">
        <p v-for="task in request.openTasks" :key="task.id">
          {{ useTaskLabels(task.type) }}
        </p>
        <p v-if="isUploadFailed" class="text-static-danger-hi body-2">
          {{ i18n(translations.uploadFailedErrorMessage) }}
        </p>
      </div>
      <p v-if="isUploadPendingOrSuccess">-</p>
    </td>
    <td class="text-wrap" :class="tableCellClassNames">
      <p class="line-clamp-2">
        {{ useFormatIssues(request.issues) }}
      </p>
    </td>
    <td class="flex text-right tabular-nums" :class="tableCellClassNames">
      <RevLink v-if="canUploadReceipt" target="_blank" :to="orderLink">
        <span class="body-1">
          {{ request.order.id }}
        </span>
      </RevLink>
      <div v-if="!canUploadReceipt">{{ request.order.id }}</div>
    </td>
    <td :class="tableCellClassNames">
      <div class="flex">
        <Flag
          v-if="showFlag"
          :aria-label="`flag-${request.order.countryCode}`"
          class="mr-4 flex-none align-text-bottom"
          :country-code="countryForFlag"
          size="l"
        />
        <span
          >{{
            i18n(translations.fullName, {
              firstName: request.customer.firstName,
              lastName: request.customer.lastName,
            })
          }}
        </span>
      </div>
    </td>
    <td :class="tableCellClassNames">
      <div v-if="canUploadReceipt">
        <RevButton
          :disabled="isUploadPending"
          :loading="isUploading"
          size="small"
          variant="secondary"
          @click.stop="uploadReceipt"
          >{{ i18n(translations.uploadReceiptButtonLabel) }}
        </RevButton>
        <input
          ref="receipt"
          accept="application/pdf"
          :aria-label="i18n(translations.uploadReceiptButtonLabel)"
          class="hidden"
          :disabled="isUploadPending"
          type="file"
          @change="handleFileChange"
          @click.stop=""
        />
      </div>
    </td>
    <td :class="tableCellClassNames">
      <IconChevronRight
        v-if="isClickable"
        :alternative-text="i18n(translations.goToConversationAriaLabel)"
      />
    </td>
  </tr>
</template>

<script setup lang="ts">
import { navigateTo, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevLink } from '@ds/components/Link'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import type { Request } from '~/api/seller-after-sales/types/requests.types'
import { ROUTE_NAMES } from '~/constants/routes'
import TaskStatus from '~/scopes/customer-care/components/TaskStatus/TaskStatus.vue'
import { useFormatDeadline } from '~/scopes/customer-care/pages/customer-care/composables/useFormatDeadline'
import { useFormatIssues } from '~/scopes/customer-care/pages/customer-care/composables/useFormatIssues'
import { useTaskLabels } from '~/scopes/customer-care/pages/customer-care/composables/useTaskLabel'
import { useUploadReceipt } from '~/scopes/customer-care/pages/customer-care/composables/useUploadReceipt'

import UploadStatus from '../../../UploadStatus/UploadStatus.vue'

import translations from './OpenTasksRow.translations'

const { request } = defineProps<{ request: Request }>()

const { FF_BO_SELLER_IS_NEW_CUSTOMER_CARE_INTERCOM_REQUEST_OPEN } =
  useRuntimeConfig().public

const i18n = useI18n()
const tracking = useTracking()
const {
  uploadFile: uploadFileReceipt,
  isUploading,
  isUploadPending,
  isUploadFailed,
  isUploadSuccess,
  isUploadPendingOrSuccess,
} = useUploadReceipt()

const receipt = ref<HTMLInputElement | null>(null)

const tableCellClassNames = 'py-24 px-16 text-left'

const hasResolutionId = computed(() => request.resolutionId !== null)
const hasTicketId = computed(() => request.ticketId !== null)

const isClickable = computed(() => hasResolutionId.value || hasTicketId.value)

const countryForFlag = computed(() => {
  const index = Object.keys(MarketCountryCode).indexOf(
    request.order.countryCode,
  )

  return Object.values(MarketCountryCode)[index]
})

const showFlag = computed(() => request.order.countryCode in MarketCountryCode)

const canUploadReceipt = computed(() => {
  return (
    request.openTasks.some((task) => task.type === 'UPLOAD_RECEIPT') &&
    !isUploadSuccess.value
  )
})

const orderLink = computed(() => {
  return {
    name: ROUTE_NAMES.ORDERS.INDEX,
    query: { orderId: request.order.id },
  }
})

async function handleRowClick() {
  if (
    hasTicketId.value &&
    FF_BO_SELLER_IS_NEW_CUSTOMER_CARE_INTERCOM_REQUEST_OPEN
  ) {
    await navigateTo({
      name: ROUTE_NAMES.CUSTOMER_CARE.REQUEST,
      params: {
        orderlinePublicId: request.order.orderlinePublicId,
      },
    })

    return
  }

  if (hasResolutionId.value) {
    await navigateTo({
      name: ROUTE_NAMES.CUSTOMER_CARE.REQUEST_RESOLUTION,
      params: {
        resolutionId: request.resolutionId,
      },
    })
  }
}
function uploadReceipt() {
  if (receipt.value !== null) {
    receipt.value.click()
  }
}

async function handleFileChange({ target }: Event) {
  const { files } = target as HTMLInputElement
  if (!files) return

  const [file] = Array.from(files)

  tracking.trackFormSubmit({
    name: 'upload-receipt',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.OPEN_TASKS,
    requestId: request.id,
    mostUrgentDeadline: useFormatDeadline(request.deadline, request.order.id),
    mostUrgentTaskType: request.openTasks[0]?.type,
  })

  const orderId = Number(request.order.id)

  await uploadFileReceipt({
    file,
    orderId,
    successMessage: i18n(translations.uploadSuccessToastLabel, {
      orderId,
    }),
    errorMessage: i18n(translations.uploadConfirmationErrorToastLabel, {
      orderId,
    }),
  })
}
</script>
